import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, ExternalLink, ShieldCheck, ShieldCheck as VerifiedIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { fetchArchiveRounds } from '../../features/archive/api';
import { EtherscanHelpSheet, etherscanUrl } from '../../features/verify/RoundAnchorCards';
import { copyText } from '../../shared/lib/clipboard';
import { GameIcon as SharedGameIcon } from '../../shared/game/GameIcon';
import { gamingRoundSeq, roundAnchorTimeZone } from '../../shared/game/roundLabel';
import type { GameCode } from '../../entities/room/types';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import type { MessageKey } from '../../shared/i18n/messages';
import { fetchMilharStatus } from '../../features/milhar/api';
import { MilharRulesSection } from './MilharRulesSection';
import { buildRulesExample, buildRulesExampleFromRound, diceFacePips } from './rulesExample';
import { rankRulesExampleRounds } from './rulesExampleChoice';

const D_SUBSCRIPTS = '₁₂₃₄₅₆₇₈₉';

// One icon language across the app — the local legacy SVG set is gone.
function GameIcon({ game }: { game: string }) {
  return <span className="rules-game-icon"><SharedGameIcon game={game as GameCode} /></span>;
}

/**
 * The gaming day to fetch the worked example from, in the anchor zone.
 *
 * A data key, not a heading: the example is labelled with a `#NNN`, and that
 * number is only meaningful within the anchor's day, so the day of rounds we ask
 * for has to be the anchor's too. It used to subtract a flat three hours, which
 * pinned it to Brazil no matter whose account was looking.
 */
function brTodayDateStr() {
  const zone = roundAnchorTimeZone();
  try {
    return new Date().toLocaleDateString('en-CA', zone ? { timeZone: zone } : undefined);
  } catch {
    return new Date().toLocaleDateString('en-CA');
  }
}

function DiceFace({ n, className }: { n: number; className: string }) {
  return (
    <span className={className}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="100%" height="100%">
        <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#fef3c7" stroke="#78350f" strokeWidth="0.6" />
        {diceFacePips(n).map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="1.7" fill="#1c1917" />
        ))}
      </svg>
    </span>
  );
}

export function RulesPage() {
  const setRoute = useAppStore((state) => state.setRoute);
  const t = useI18nStore((state) => state.t);
  // Prefer a REAL recent round for the walkthrough — its block numbers and
  // hashes actually exist on Etherscan. The synthetic example is only the
  // offline fallback.
  const liveQuery = useQuery({
    queryKey: ['rules-live-example'],
    queryFn: () => fetchArchiveRounds(brTodayDateStr(), 48),
    staleTime: 300_000,
    retry: false,
  });
  // 整点档 status 在这里只为一件事：告诉我们最近哪些期**真的**是整点档开奖。
  // queryKey 与 MilharRulesSection 里那条完全一致，react-query 去重，只发一次请求。
  const horaQuery = useQuery({
    queryKey: ['milhar-rules-config', '5'],
    queryFn: () => fetchMilharStatus(null, '5'),
    staleTime: 300_000,
    retry: false,
  });
  // 整页共用一期真实的整点档开奖 —— 它同时也是一个 5 分钟期，所以区块演算、
  // Milhar 卡片、Milhar Hora 卡片可以共用同一期，三处展示的都是真开出来过的。
  const picked = useMemo(() => {
    const rounds = [...(liveQuery.data?.rounds || [])]
      .sort((a, b) => Number(b.cutoff_ts || b.ends_at || 0) - Number(a.cutoff_ts || a.ends_at || 0));
    const ranked = rankRulesExampleRounds(
      rounds,
      (horaQuery.data?.recentResults || []).map((result) => result.round),
    );
    for (const candidate of ranked) {
      const built = buildRulesExampleFromRound(candidate.round);
      if (built) return { ex: built, hourlyAnchor: candidate.hourlyAnchor };
    }
    return { ex: buildRulesExample(), hourlyAnchor: false };
  }, [liveQuery.data, horaQuery.data]);
  const ex = picked.ex;
  const [etherscanBlock, setEtherscanBlock] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  const tr = (key: MessageKey, params?: Record<string, string | number>) => {
    let out = t(key);
    if (params) for (const [k, v] of Object.entries(params)) out = out.split(`%${k}%`).join(String(v));
    return out;
  };

  return (
    <section className="rules-legacy-page">
      <header className="rules-legacy-header">
        <button className="rules-legacy-back" type="button" onClick={() => setRoute('lobby')} aria-label={t('backToLobby')}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <div className="rules-legacy-title">{t('rulesHeader')}</div>
      </header>

      <div className="rules-legacy-scroll">
        {/* Trust hero */}
        <div className="trust-hero">
          <div className="trust-hero-row">
            <span className="trust-hero-shield">
              <ShieldCheck size={26} aria-hidden="true" />
            </span>
            <h2 className="trust-hero-title">{t('trustHeroTitle')}</h2>
          </div>
          <p className="trust-hero-body">{t('trustHeroBody')}</p>
        </div>

        {/* One-liner */}
        <div className="trust-oneliner">
          <span className="trust-oneliner-emoji">💡</span>
          <div className="trust-oneliner-text">
            <div className="trust-oneliner-label">{t('trustOneLinerLabel')}</div>
            <div className="trust-oneliner-quote">{t('trustOneLinerQuote')}</div>
          </div>
        </div>

        <div className="rules-walkthrough">
          {/* PART 1 */}
          <div className="rules-part">
            <div className="rules-part-header">
              <span className="rules-part-num">1</span>
              <span className="rules-part-title">{t('rulesPart1TitleShort')}</span>
            </div>
            <p className="rules-part-intro">{t('rulesPart1Intro')}</p>

            <div className="rules-example-provenance">
              {tr(ex.isLive ? 'rulesLiveHeader' : 'rulesExampleHeader', {
                // 与归档页同一套编号 —— 玩家能拿这个号去归档里找到同一期。
                // 全局期号（#40236）只在链上核对时才有意义，那由区块号承担。
                round: gamingRoundSeq(ex.cutoffTs) || String(ex.round),
                date: ex.dateLabel,
              })}
            </div>

            {ex.blocks.map((b, bi) => (
              <div key={bi} className={`rules-block-card rules-block-${bi + 1}`}>
                <div className="rules-block-card-header">
                  <span className={`rules-block-num rules-block-num-${bi + 1}`}>{bi + 1}</span>
                  <div className="rules-block-card-meta">
                    <span className="rules-block-card-title">{tr('rulesBlockN', { n: bi + 1 })}</span>
                    <span className="rules-block-card-sub">
                      #{b.number}
                      <span className="rules-block-time">
                        <span className="rules-block-time-tag rules-block-time-br">{t('localTimeTag')}</span>
                        {b.timeBR}
                      </span>
                      <span className="rules-block-time">
                        <span className="rules-block-time-tag rules-block-time-utc">UTC</span>
                        {b.timeUTC}
                      </span>
                    </span>
                  </div>
                  <span className={`rules-block-example-tag${ex.isLive ? ' is-real' : ''}`}>{t(ex.isLive ? 'rulesRealTag' : 'rulesBlockExampleTag')}</span>
                  {ex.isLive && b.number ? (
                    <button className="rules-block-etherscan tap" type="button" aria-label={t('verifyEtherscan')} onClick={() => setEtherscanBlock(b.number)}>
                      <ExternalLink size={14} aria-hidden="true" />
                    </button>
                  ) : null}
                </div>

                <div className="rules-block-hash-label">{t('rulesPart1HashLabel')}</div>
                <div
                  className={`rules-block-hash${ex.isLive && b.number ? ' tap' : ''}`}
                  {...(ex.isLive && b.number ? { role: 'button', tabIndex: 0, onClick: () => setEtherscanBlock(b.number), style: { cursor: 'pointer' } } : {})}
                >
                  0x
                  {b.chars.map((ch, ci) => (
                    <span
                      key={ci}
                      className={ch.isDigit ? (ch.inLast10 ? 'rules-hash-last10' : 'rules-hash-digit') : 'rules-hash-letter'}
                    >
                      {ch.c}
                    </span>
                  ))}
                </div>
                <div className="rules-block-hash-hint">{t('rulesPart1HashHint')}</div>

                <div className="rules-block-extract-label">{t('rulesPart1ExtractLabel')}</div>
                <div className="rules-digit-row">
                  {b.last10.map((d, di) => (
                    <span key={di} className={`rules-digit-box rules-digit-box-${bi + 1}`}>
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* PART 2 */}
          <div className="rules-part">
            <div className="rules-part-header">
              <span className="rules-part-num">2</span>
              <span className="rules-part-title">{t('rulesPart2TitleShort')}</span>
            </div>
            <p className="rules-part-intro">{t('rulesPart2Intro')}</p>

            <div className="rules-d-table">
              {ex.blocks.map((b, bi) => (
                <div key={bi} className={`rules-d-row rules-d-row-b${bi + 1}`}>
                  <span className="rules-d-label">B{bi + 1}</span>
                  {b.last10.map((d, di) => (
                    <span key={di} className="rules-d-cell">
                      {d}
                    </span>
                  ))}
                </div>
              ))}
              <div className="rules-d-row rules-d-row-sum">
                <span className="rules-d-label">{t('rulesPart2SumLabel')}</span>
                {ex.sums.map((s, si) => (
                  <span key={si} className={`rules-d-cell rules-d-cell-sum${s > 9 ? ' rules-d-cell-overflow' : ''}`}>
                    {s}
                  </span>
                ))}
              </div>
              <div className="rules-d-fold-hint">{t('rulesPart2FoldHint')}</div>
              <div className="rules-d-row rules-d-row-result">
                <span className="rules-d-label">{t('rulesPart2ModLabel')}</span>
                {ex.D.map((d, di) => (
                  <span key={di} className="rules-d-cell rules-d-cell-result">
                    {d}
                  </span>
                ))}
              </div>
              <div className="rules-d-row rules-d-row-dlabels">
                <span className="rules-d-label">&nbsp;</span>
                {Array.from({ length: 10 }, (_, i) => (
                  <span key={i} className="rules-d-col-label rules-d-dlabel">
                    {i < 9 ? `D${D_SUBSCRIPTS[i]}` : 'D₁₀'}
                  </span>
                ))}
              </div>
            </div>

            <div className="rules-explain-callout">
              <span className="rules-explain-icon">💡</span>
              <div className="rules-diy-body-wrap">
                <div className="rules-explain-title">{t('rulesPart2ExplainTitle')}</div>
                <p className="rules-explain-body">{t('rulesPart2ExplainBody')}</p>
              </div>
            </div>
          </div>

          {/* PART 3 */}
          <div className="rules-part">
            <div className="rules-part-header">
              <span className="rules-part-num">3</span>
              <span className="rules-part-title">{t('rulesPart3TitleShort')}</span>
            </div>
            <p className="rules-part-intro">{t('rulesPart3Intro')}</p>

            {/* 两个彩票玩法排在最前面。理由有两条：一是它们是首页 ladder 主推的，
                排在第 5 屏等于没人看得到；二是叙事上「直接读表」比「读表再算一道」
                简单，从简单的讲起，后面三个玩法的公式才显得是加法而不是门槛。 */}
            <MilharRulesSection digits={ex.D} cutoffTs={ex.cutoffTs} hourlyAnchor={picked.hourlyAnchor} />

            {/* 房间三玩法的引子。彩票段自己带引子和一个金色的 Acumulado 收尾，
                后面直接接「Último Dígito」会像话没说完就换了台。 */}
            <div className="mr-lead mr-lead-room">
              <div className="mr-lead-title">{t('mrulesRoomLeadTitle')}</div>
              <p className="mr-lead-body">{t('mrulesRoomLeadBody')}</p>
            </div>

            {/* Last-Digit */}
            <div className="rules-game-card">
              <div className="rules-game-header">
                <GameIcon game="lastdigit" />
                <span className="rules-game-title">{t('rulesGameLDTitle')}</span>
              </div>
              <p className="rules-game-rule">{t('rulesGameLDRule')}</p>
              <div className="rules-game-d-display">
                <span className="rules-game-d-label">D₁ =</span>
                <span className="rules-result-box rules-result-box-lg">{ex.lastdigit}</span>
                <span className="rules-game-d-note">{t('rulesGameLDD1Note')}</span>
              </div>
              <ul className="rules-game-outcomes">
                <li>{ex.ldOddEven === 'odd' ? t('rulesGameLDOdd') : t('rulesGameLDEven')}</li>
                <li>{ex.ldBigSmall === 'big' ? t('rulesGameLDBig') : t('rulesGameLDSmall')}</li>
                <li>{tr('rulesGameLDExact', { d: ex.lastdigit })}</li>
              </ul>
            </div>

            {/* Sicbo */}
            <div className="rules-game-card">
              <div className="rules-game-header">
                <GameIcon game="sicbo" />
                <span className="rules-game-title">{t('rulesGameSicboTitle')}</span>
              </div>
              <p className="rules-game-rule">{t('rulesGameSicboRule')}</p>

              {ex.sicboSteps.map((step) => (
                <div key={step.idx} className="rules-sicbo-step">
                  <div className="rules-sicbo-step-body">
                    <div className="rules-sicbo-trio">
                      {t(`rulesGameSicboTrio${step.idx}` as MessageKey)} = {step.m}
                    </div>
                    <div className="rules-sicbo-modline">
                      {step.m} ÷ 6 = {step.q}, {t('rulesPart2Sobra')} {step.r}
                    </div>
                    <div className="rules-sicbo-resultline">
                      {t('rulesGameSicboDieLabel')} {step.idx}: {step.r} + 1 =
                    </div>
                  </div>
                  <DiceFace n={step.die} className="rules-die-result" />
                </div>
              ))}

              <div className="rules-sicbo-sum">
                <span className="rules-sicbo-sum-label">{t('rulesGameSicboSumLabel')}:</span>
                <span className="rules-sicbo-sum-formula">
                  {ex.dice.map((d, i) => (
                    <span key={i} className="inline-flex items-center">
                      <DiceFace n={d} className="rules-die-inline" />
                      {i < 2 ? <span className="mx-1.5 text-ink-2">+</span> : null}
                    </span>
                  ))}
                  <span className="ml-2 text-ink-2">=</span>
                  <strong className="ml-2 text-gold-300">{ex.sicboSum}</strong>
                </span>
              </div>
              <ul className="rules-game-outcomes">
                <li>{t('rulesGameSicboBig')}</li>
                <li>{t('rulesGameSicboSmall')}</li>
                <li>{t('rulesGameSicboTriple')}</li>
              </ul>
            </div>

            {/* Roulette */}
            <div className="rules-game-card">
              <div className="rules-game-header">
                <GameIcon game="roulette" />
                <span className="rules-game-title">{t('rulesGameRouletteTitle')}</span>
              </div>
              <p className="rules-game-rule">{t('rulesGameRouletteRule')}</p>

              <div className="rules-roulette-line rules-roulette-line-labeled">
              </div>
              {/* 主线只有一条除法：十位数 ÷ 37。以前这里先画 M₁+M₂+M₃ 的三项
                  网格再除，读者要先接受「为什么切成三段」才能往下读；那个分
                  组本来是 Sic Bo 的需要，轮盘只是搭了便车（2026-09-24 PM）。 */}
              <div className="rules-roulette-line">
                {tr('rulesGameRouletteModLine', { d: ex.dNumber, q: ex.rouletteQ, r: ex.rouletteR })}
              </div>
              {/* 副行：手算捷径。十位数除以 37 没人能用纸笔核，四段相加能，
                  而且因为 999 = 27 × 37，两者余数必然相同。 */}
              <div className="rules-roulette-handcheck">
                {tr('rulesGameRouletteHandCheck', {
                  parts: `${ex.D[0]} + ${ex.m1} + ${ex.m2} + ${ex.m3}`,
                  sum: ex.handSum,
                  r: ex.rouletteR,
                })}
              </div>

              <div className="rules-game-d-display rules-game-d-display-roulette">
                <span className={`rules-roulette-pocket rules-roulette-pocket-${ex.rouletteColor}`}>{ex.rouletteNum}</span>
                <span className={`rules-roulette-color-chip rules-roulette-color-chip-${ex.rouletteColor}`}>
                  {(ex.rouletteColor === 'red'
                    ? t('rulesGameRouletteRed')
                    : ex.rouletteColor === 'black'
                      ? t('rulesGameRouletteBlack')
                      : t('rulesGameRouletteGreen')
                  ).toUpperCase()}
                </span>
              </div>
              <ul className="rules-game-outcomes">
                <li>{t('rulesGameRoulettePay1')}</li>
                <li>{t('rulesGameRoulettePay2')}</li>
                <li>{t('rulesGameRoulettePay3')}</li>
                <li>{t('rulesGameRoulettePay4')}</li>
              </ul>
            </div>

            <button className="trust-example-cta" type="button" onClick={() => setRoute('archive')}>
              <span>{t('trustExampleCta')}</span>
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Raffle (Sorteios) now has its own dedicated fairness/how-it-works
            surfaces (the Draws how-it-works sheet + the sorteios.fair page), so
            the daily-raffle panel was removed from this betting-games rules page
            to avoid duplicate, out-of-context content. */}

        {/* FAQ */}
        <p className="rules-fork-note" data-rules-fork-note="true">{t('rulesForkNote')}</p>

        <div className="trust-faq">
          <div className="trust-faq-header">{t('trustFaqHeader')}</div>
          {[1, 2, 3, 4].map((i) => (
            <details key={i} className="trust-faq-item">
              <summary className="trust-faq-q">
                <span className="trust-faq-q-icon">❓</span>
                <span className="trust-faq-q-text">{t(`trustFaq${i}Q` as MessageKey)}</span>
                <span className="trust-faq-q-chevron">
                  <ChevronDown size={14} aria-hidden="true" />
                </span>
              </summary>
              <div className="trust-faq-a">{t(`trustFaq${i}A` as MessageKey)}</div>
            </details>
          ))}
        </div>

        <a
          href="https://sorteia.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="rules-live-link"
        >
          <VerifiedIcon size={16} aria-hidden="true" />
          <span>{t('seeLiveDraws')}</span>
          <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>

      {etherscanBlock ? (
        <EtherscanHelpSheet
          blockNumber={etherscanBlock}
          feedback={copyFeedback}
          onCopy={async () => {
            const ok = await copyText(etherscanUrl(etherscanBlock));
            setCopyFeedback(t(ok ? 'copySucceeded' : 'copyFailed'));
            window.setTimeout(() => setCopyFeedback(''), 1800);
          }}
          onOpen={() => window.open(etherscanUrl(etherscanBlock), '_blank', 'noopener')}
          onClose={() => { setEtherscanBlock(null); setCopyFeedback(''); }}
        />
      ) : null}
    </section>
  );
}
